import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Hourglass, X, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Panel, EstadoBadge, TIPO_COLOR, fmtFecha } from "./portalShared";
import { AusenciaDialog } from "./AusenciaDialog";
import { ConsumoHorasDialog } from "./ConsumoHorasDialog";

const CLASE_LABEL = { correccion: "Corrección fichaje", ausencia: "Ausencia", horas: "Horas acumuladas" };
const CLASE_COLOR = { correccion: "bg-amber-500/10 text-amber-300 border-amber-500/30", ausencia: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", horas: "bg-indigo-500/10 text-indigo-200 border-indigo-500/30" };

export function SolicitudesPanel({ caps, saldo, meta, onCambio }) {
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [dlgAus, setDlgAus] = useState(false);
  const [dlgHoras, setDlgHoras] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/portal-empleado/solicitudes"); setItems(data); }
    catch { toast.error("Error cargando solicitudes"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelar = async (s) => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try {
      if (s.clase === "ausencia") await api.post(`/rrhh/ausencias/${s.id}/cancelar`);
      else if (s.clase === "horas") await api.post(`/portal-empleado/solicitudes-horas/${s.id}/cancelar`);
      toast.success("Solicitud cancelada"); load(); onCambio && onCambio();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error cancelando"); }
  };

  const lista = (items || []).filter((s) => filtro === "todas" || s.clase === filtro);
  return (
    <div className="space-y-4" data-testid="solicitudes-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {["todas", "correccion", "ausencia", "horas"].map((f) => (
          <button key={f} onClick={() => setFiltro(f)} data-testid={`filtro-${f}`}
            className={`text-xs px-3 py-1 rounded-full border ${filtro === f ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "bg-transparent text-zinc-400 border-zinc-700"}`}>
            {f === "todas" ? "Todas" : CLASE_LABEL[f]}
          </button>
        ))}
        <span className="flex-1" />
        {caps?.ausencias && (
          <Button size="sm" onClick={() => setDlgAus(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-8 text-xs" data-testid="btn-nueva-ausencia">
            <Plus className="w-3.5 h-3.5 mr-1" /> Solicitar ausencia
          </Button>
        )}
        {caps?.consumo_horas && (
          <Button size="sm" onClick={() => setDlgHoras(true)} className="bg-indigo-500 text-zinc-950 hover:bg-indigo-400 h-8 text-xs" data-testid="btn-consumo-horas">
            <Hourglass className="w-3.5 h-3.5 mr-1" /> Consumir horas
          </Button>
        )}
      </div>

      <Panel title={`Mis solicitudes (${lista.length})`} icon={ClipboardList} testid="solicitudes-lista">
        {items === null ? <div className="p-6 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : lista.length === 0 ? <div className="p-6 text-center text-zinc-500 text-sm" data-testid="solicitudes-vacio">No tienes solicitudes.</div> : (
            <div className="divide-y divide-zinc-800/60">
              {lista.map((s) => (
                <div key={`${s.clase}-${s.id}`} className="px-3 sm:px-4 py-2.5 flex items-start gap-3" data-testid={`solicitud-${s.id}`}>
                  <div className="shrink-0 pt-0.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${s.clase === "ausencia" ? (TIPO_COLOR[s.tipo] || CLASE_COLOR.ausencia) : CLASE_COLOR[s.clase]}`}>{s.label}</span>
                  </div>
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-300 capitalize">{fmtFecha(s.fecha)}{s.fecha_fin && s.fecha_fin !== s.fecha ? ` → ${fmtFecha(s.fecha_fin)}` : ""}</span>
                      <EstadoBadge estado={s.estado} />
                      {s.impacto && <span className="text-[11px] text-zinc-400">{s.impacto}</span>}
                    </div>
                    {s.detalle && <div className="text-xs text-zinc-500 mt-0.5 truncate">"{s.detalle}"</div>}
                    {s.aplicacion?.length > 0 && s.estado === "aprobada" && (
                      <div className="text-[10px] text-indigo-200/80 mt-0.5">Aplicado a: {s.aplicacion.map((a) => `${a.periodo} (${a.horas} h)`).join(", ")}</div>
                    )}
                    {s.resuelta_at && (
                      <div className="text-[11px] text-zinc-400 mt-1" data-testid={`resolucion-${s.id}`}>
                        {s.estado} el {new Date(s.resuelta_at).toLocaleDateString("es-ES")}{s.resuelta_by_nombre ? ` por ${s.resuelta_by_nombre}` : ""}
                        {s.comentario_admin && <span className="text-zinc-300"> · "{s.comentario_admin}"</span>}
                      </div>
                    )}
                  </div>
                  {s.cancelable && (
                    <Button size="sm" variant="ghost" onClick={() => cancelar(s)} className="h-7 text-xs text-zinc-400 hover:text-red-400 shrink-0" data-testid={`cancelar-${s.id}`}>
                      <X className="w-3 h-3 mr-1" /> Cancelar
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
      </Panel>

      <AusenciaDialog open={dlgAus} onClose={() => setDlgAus(false)} meta={meta} onSaved={() => { load(); onCambio && onCambio(); }} />
      <ConsumoHorasDialog open={dlgHoras} onClose={() => setDlgHoras(false)} saldo={saldo} onSaved={() => { load(); onCambio && onCambio(); }} />
    </div>
  );
}
